import { Box, Button, Card, CardHeader, Divider, Table, TableBody, TableCell, TableRow, TextField, Typography } from "@mui/material";
import { useGetList, useRefresh, useSaveContext } from "ra-core";
import { useEffect } from "react";
import { useCallback } from "react";
import { FC, useState } from "react";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_BACKUP_JOBS } from "@/constants";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { RecPatch } from "@/dataProvider/tools";
import { EnumOptionResponseDto } from "@/gen/api/backup";
import { useDatabaseInfoContext } from "./DatabaseInfoContext";


const BackupRetentionScheduleCard: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_BACKUP_JOBS])
  const { databaseInfo: database, deploymentInfo } = useDatabaseInfoContext()
  const [retentionDayId, setRetentionDayId] = useState<number | undefined>(database?.retentionDaysId);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const refresh = useRefresh()
  const onUpdateRetentionDays = useSimpleSaveAction()
  const { saving: updating } = useSaveContext()

  const { data: retentionDaysOptionsMap } = useGetList<EnumOptionResponseDto & RecPatch>('backup-retention-days')
  const retentionDaysOptions = Object.values(retentionDaysOptionsMap || {}).sort((a, b) => a.id - b.id)

  useEffect(() => {
    if (database) {
      setRetentionDayId(database.retentionDaysId)
    }
  }, [database?.retentionDaysId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddInit = (): void => {
    setIsExpanded(true);
  };

  const handleAddCancel = useCallback((): void => {
    setRetentionDayId(database?.retentionDaysId)
    setIsExpanded(false);
  }, [setRetentionDayId, setIsExpanded, database?.retentionDaysId]);

  const handleAddConfirm = useCallback(() => {
    if (retentionDayId) {
      const payload = { 
        retentionDaysId: retentionDayId, 
        databaseId: database?.databaseId, 
        name: database?.name || undefined, 
        engineId: database?.engineId 
      }
      onUpdateRetentionDays(payload, {
        onSuccess: () => { 
          setIsExpanded(false) 
          refresh()
        },
      })
    }
  }, [retentionDayId, onUpdateRetentionDays, setIsExpanded, refresh, database])

  return (
    <Card>
      <CardHeader title="Backup Retention Schedule" id="retention-schedule-header" />
      <Divider />
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography
                color="textPrimary"
                variant="subtitle2"
                id="duration-days-title"
              >
                Duration (days)
              </Typography>
            </TableCell>
            <TableCell>
              {hasWritePermit && isExpanded ?
              <TextField
                id="backup-retention-select"
                fullWidth
                name="option"
                onChange={(e) => setRetentionDayId(+e.target.value)}
                size="small"
                select
                SelectProps={{ 
                  native: true,
                  inputProps: {
                    'aria-labelledby': 'duration-days-title backup-retention-select',
                  }
                }}
                value={retentionDayId}
                variant="outlined"
              >
                {retentionDaysOptions.map(r => (
                  <option key={r.key} value={r.key}>{r.text}</option>
                ))}
              </TextField> :
              <Typography
                color="textSecondary"
                variant="body2"
              >
                {retentionDayId ? retentionDaysOptions.find(r => r.key === retentionDayId)?.text : null}
              </Typography>
              }
            </TableCell>
          </TableRow>
          {/* <TableRow>
            <TableCell>
              <Typography
                color="textPrimary"
                variant="subtitle2"
              >
                Estimated Billing Amount ($/month)
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                color="textSecondary"
                variant="body2"
              >
                $30 - test
              </Typography>
            </TableCell>
          </TableRow> */}
        </TableBody>
      </Table>
      {hasWritePermit && database && (!database.isManaged || (deploymentInfo && !deploymentInfo.isDeleted)) &&
        <Box
          sx={{
            display: 'flex',
            justifyContent: isExpanded ? 'space-between' : 'center',
            m: 1
          }}
        >
        {
          isExpanded
          ? (
            <>
              <Button
                color="primary"
                disabled={updating}
                onClick={handleAddCancel}
                variant="text"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={updating}
                onClick={handleAddConfirm}
                variant="contained"
              >
                Save
              </Button>
            </>
          )
          : (
            <Button
              aria-describedby="retention-schedule-header"
              color="primary"
              onClick={handleAddInit}
              variant="text"
            >
              Update
            </Button>
          )
        }
        </Box>
      }
    </Card>
  )
}

export default BackupRetentionScheduleCard;
